import { useEffect, RefObject } from 'react';

type UseClickOutsideProps = {
    ref: RefObject<HTMLElement | null>;
    isActive: boolean;
    onClickOutside: () => void;
    excludeRefs?: RefObject<HTMLElement | null>[];
};

/**
 * Хук для обработки кликов вне элемента и нажатий клавиши Escape
 * @param ref - ссылка на DOM элемент
 * @param isActive - активен ли обработчик
 * @param onClickOutside - колбэк для вызова при клике вне элемента или нажатии Escape
 * @param excludeRefs - массив ссылок на элементы, клики на которые не должны вызывать onClickOutside
 */
export const useClickOutside = ({ ref, isActive, onClickOutside, excludeRefs }: UseClickOutsideProps) => {
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // Проверяем, что клик не внутри основного элемента
            if (ref.current && !ref.current.contains(target)) {
                // Проверяем, что клик не внутри исключенных элементов
                const isClickOnExcludedElement = excludeRefs?.some(
                    excludeRef => excludeRef.current && excludeRef.current.contains(target),
                );

                if (!isClickOnExcludedElement) {
                    onClickOutside();
                }
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClickOutside();
            }
        };

        if (isActive) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isActive, onClickOutside, ref, excludeRefs]);
};
